<template>
    <div ref="refRoot" class="slideToRip" @pointerdown.stop.prevent="onPointerDown">
        <div ref="refContainer" class="slideToRip__container">
            <span class="slideToRip__trackBg fill" />
            <span class="slideToRip__trackOutline fill" />
            <span ref="refStroke" class="slideToRip__trackStroke fill" />

            <div class="slideToRip__chevrons">
                <div
                    v-for="(chevron, index) in chevrons"
                    :key="index"
                    class="slideToRip__chevronWrapper"
                >
                    <SvgIcon name="arrowhead-right" class="slideToRip__chevron" />
                </div>
            </div>

            <div ref="refHandle" class="slideToRip__handle">
                <div class="slideToRip__handleBg fill"></div>
                <span class="slideToRip__text">Slide To Rip</span>
            </div>
        </div>
    </div>
</template>

<script setup>
    import gsap from 'gsap';
    import { computed, onBeforeUnmount, ref, watch } from 'vue';

    import { useClassToggle, useRafBool } from '@resn/gozer-vue';
    import { SvgIcon } from '@resn/gozer-vue/components';

    import { useBtnStrokeAnimation } from '~/composables/useBtnStrokeAnimation';

    const props = defineProps({
        enabled: {
            type: Boolean,
            default: false,
        },
        progress: {
            type: Number,
            default: 0,
        },
    });

    const refRoot = ref(null);
    const refContainer = ref(null);
    const refHandle = ref(null);
    const refStroke = ref(null);

    const { setStrokeActive } = useBtnStrokeAnimation(refStroke);

    const chevrons = Array(20).fill(null);

    // Container and handle dimensions (matching CSS values)
    const containerWidth = 35; // rem
    const handleWidth = 17; // rem
    const padding = 0; // rem (left and right)

    const containerWidthRem = `${35}rem`;
    const handleWidthRem = `${17}rem`;
    const paddingRem = `${0}rem`;

    // Maximum translation distance: container width - handle width - left padding - right padding
    const maxTranslate = containerWidth - handleWidth - padding * 2;

    const active = ref(false);

    const update = () => {
        refHandle.value.style.transform = `translateX(${props.progress * maxTranslate}rem)`;
    };

    useRafBool(active, update);

    const setVisible = (val) => {
        active.value = val;

        gsap.killTweensOf(refContainer.value, 'autoAlpha, scale');
        gsap.to(refContainer.value, {
            scale: val ? 1 : 0.5,
            autoAlpha: val ? 1 : 0,
            duration: val ? 0.8 : 0.3,
            ease: val ? 'elastic.out(1.2, 1)' : 'elastic.in(1, 1)',
            onComplete: () => (active.value = val),
        });

        setStrokeActive(val);
    };

    const emit = defineEmits(['dragstart', 'drag', 'dragend']);

    let activeMoveHandler = null;
    let activeUpHandler = null;

    const onPointerDown = (e) => {
        if (!props.enabled) return;

        const containerRect = refContainer.value.getBoundingClientRect();
        const handleRect = refHandle.value.getBoundingClientRect();
        const maxDragPx = containerRect.width - handleRect.width;
        const startX = e.clientX;
        const startProgress = props.progress;
        let progress = 0;

        emit('dragstart');

        const onPointerMove = (e) => {
            e.preventDefault();
            const delta = e.clientX - startX;
            progress = Math.max(0, Math.min(1, startProgress + delta / maxDragPx));
            emit('drag', progress);
        };

        const onPointerUp = () => {
            emit('dragend');

            window.removeEventListener('pointermove', onPointerMove);
            window.removeEventListener('pointerup', onPointerUp);
            activeMoveHandler = null;
            activeUpHandler = null;
        };

        activeMoveHandler = onPointerMove;
        activeUpHandler = onPointerUp;

        window.addEventListener('pointermove', onPointerMove);
        window.addEventListener('pointerup', onPointerUp);
    };

    onBeforeUnmount(() => {
        if (activeMoveHandler) window.removeEventListener('pointermove', activeMoveHandler);
        if (activeUpHandler) window.removeEventListener('pointerup', activeUpHandler);
    });

    const enabled = computed(() => props.enabled);
    watch(enabled, setVisible);

    useClassToggle(refRoot, { enabled });
</script>

<style lang="scss" scoped>
    @use '@/styles/app/btn-stroke.scss' as *;
    @use '@/styles/shared.scss' as *;

    .slideToRip {
        --height: 7.2rem;
        --padding: v-bind(paddingRem);
        --container-width: v-bind(containerWidthRem);
        --handle-width: v-bind(handleWidthRem);

        position: absolute;
        left: 50%;
        bottom: 8%;
        transform: translateX(-50%);
        z-index: 100;
        pointer-events: none;
        cursor: pointer;
        contain: layout;

        &.enabled {
            pointer-events: auto;
        }

        &__container {
            opacity: 0;
            visibility: hidden;
            transform: scale(0.5);

            position: relative;
            width: var(--container-width);
            height: var(--height);
            padding: var(--padding);
            border-radius: var(--height);
            overflow: hidden;
        }

        &__trackBg {
            @include btn-chrome-gradient-fill;
        }

        &__trackOutline {
            @include btn-chrome-outline-border;
        }

        &__trackStroke {
            @include btn-stroke-animated-ring;
        }

        &__handle {
            @include flexCenter;

            position: absolute;
            top: var(--padding);
            left: var(--padding);
            width: var(--handle-width);
            height: calc(100% - var(--padding) * 2);

            transition: clip-path 0.15s ease-out;
            z-index: 2;

            clip-path: inset(0.8rem round calc(var(--height) / 2));

            .slideToRip:hover & {
                clip-path: inset(0.6rem round calc(var(--height) / 2));
            }

            .slideToRip:active & {
                clip-path: inset(0.8rem round calc(var(--height) / 2));
            }
        }

        &__handleBg {
            background: white;
            border-radius: var(--height);
        }

        &__text {
            color: black;
            white-space: nowrap;
            position: relative;

            @include textBtn;
        }

        &__chevrons {
            @include fill;

            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 0 3rem;
            contain: content;
        }

        &__chevronWrapper {
            // animation: highlight 5s cubic-bezier(0.4, 0, 0.2, 1) infinite;
            // animation-delay: var(--delay);
            // will-change: opacity;

            width: 1rem;
            height: 2rem;

            @for $i from 1 through 20 {
                &:nth-child(#{$i}) {
                    --delay: #{($i - 1) * 0.2}s;
                }
            }
        }

        &__chevron {
            font-size: 2.4rem;
            color: white;
            font-weight: 400;
            line-height: 1;
            width: 1rem;
            height: 2rem;
            stroke-width: 0.1rem;
            stroke: white;
            fill: transparent;
        }
    }

    @keyframes highlight {
        20% {
            opacity: 0.3;
        }
        50% {
            opacity: 1;
        }
        80% {
            opacity: 0.3;
        }
    }
</style>
